// FloatingWindow.js
//
// Build 28 (item 1): reusable window chrome. Pure presentation +
// pointer interaction; knows nothing about its contents. Desktop
// renders one per entry in useWindowManager's list and drops the
// appropriate viewer (NoteViewer / EntityViewer / DiagramViewer) in
// as children.

import React, { useRef, useCallback } from 'react';
import './FloatingWindow.css';

const MIN_W = 320;
const MIN_H = 220;

function FloatingWindow({
  win,            // { id, title, x, y, w, h, z }
  onFocus,        // (id) => void
  onClose,        // (id) => void
  onMinimize,     // (id) => void
  onGeometry,     // (id, {x,y,w,h}) => void
  children
}) {
  const dragState = useRef(null);

  const onDragMove = useCallback((e) => {
    const st = dragState.current;
    if (!st) return;
    if (st.mode === 'move') {
      const nx = st.origX + (e.clientX - st.startX);
      const ny = st.origY + (e.clientY - st.startY);
      onGeometry(win.id, { x: Math.max(0, nx), y: Math.max(0, ny) });
    } else if (st.mode === 'resize') {
      const nw = Math.max(MIN_W, st.origW + (e.clientX - st.startX));
      const nh = Math.max(MIN_H, st.origH + (e.clientY - st.startY));
      onGeometry(win.id, { w: nw, h: nh });
    }
  }, [win.id, onGeometry]);

  const onDragEnd = useCallback(() => {
    dragState.current = null;
    window.removeEventListener('mousemove', onDragMove);
    window.removeEventListener('mouseup', onDragEnd);
  }, [onDragMove]);

  const onDragStart = useCallback((e) => {
    if (e.button !== 0) return;
    if (e.target.closest('.fw-controls')) return;
    onFocus(win.id);
    dragState.current = {
      mode: 'move',
      startX: e.clientX, startY: e.clientY,
      origX: win.x, origY: win.y
    };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    e.preventDefault();
  }, [win, onFocus, onDragMove, onDragEnd]);

  const onResizeStart = useCallback((e) => {
    if (e.button !== 0) return;
    onFocus(win.id);
    dragState.current = {
      mode: 'resize',
      startX: e.clientX, startY: e.clientY,
      origW: win.w, origH: win.h
    };
    window.addEventListener('mousemove', onDragMove);
    window.addEventListener('mouseup', onDragEnd);
    e.preventDefault();
    e.stopPropagation();
  }, [win, onFocus, onDragMove, onDragEnd]);

  return (
    <div
      className="floating-window"
      style={{
        left: win.x, top: win.y,
        width: win.w, height: win.h,
        zIndex: 1000 + (win.z || 0)
      }}
      onMouseDown={() => onFocus(win.id)}
    >
      <div className="fw-titlebar" onMouseDown={onDragStart}>
        <span className="fw-title" title={win.title}>{win.title}</span>
        <div className="fw-controls">
          <button
            className="fw-btn fw-min"
            title="Minimize"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onMinimize(win.id); }}
          >
            –
          </button>
          <button
            className="fw-btn fw-close"
            title="Close"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => { e.stopPropagation(); onClose(win.id); }}
          >
            ×
          </button>
        </div>
      </div>

      <div className="fw-body">
        {children}
      </div>

      <div
        className="fw-resize-grip"
        onMouseDown={onResizeStart}
        title="Resize"
      />
    </div>
  );
}

export default FloatingWindow;
